/* eslint-disable quotes */
import { Dispatch, ReactNode, SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import {
  SERVICE_ENTITLEMENTS_MAPPING,
  SERVICES,
} from 'utils/service-entitlements-config';

// Types
import { WatchlistCategoryType } from 'services/watchlist/types';

export type DataItemType = {
  id: WatchlistCategoryType;
  icon: string;
  maxMonitoredItems: number;
  entitlement: string[];
  qaSelector: string;
};

type CategoryDataType = {
  title: string;
  data: DataItemType[];
  qaSelector: string;
};

export type WatchlistDataType = {
  personalInfromation: CategoryDataType;
  identification: CategoryDataType;
  financial: CategoryDataType;
  userName: CategoryDataType;
};

type DarkWebItemsMapType = {
  // eslint-disable-next-line no-unused-vars
  [K in WatchlistCategoryType]: {
    title: string;
    itemType: string;
    addItemTitle: string;
    whyMonitor: ReactNode;
    addItemSuccessMsg: string;
    addItemFailureMsg: string;
    removeItemTitle: string;
    removeItemSubTitle: string;
    removeItemSuccessMsg: string;
    removeItemFailureMsg: string;
  };
};

type EmailVerificationModalProps = {
  length: number;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
};

/* Component to render the FAQ route link */
function FAQLink() {
  return (
    <Link to='/faqs/darkwebFaqs' className='linkText'>
      FAQ
    </Link>
  );
}

export function ReviewLink({
  length,
  setIsModalOpen,
}: EmailVerificationModalProps) {
  const handleReviewBtn = () => {
    setIsModalOpen(true);
  };

  if (!length) return null;
  return (
    <div>
      Verification required for {length}
      {length === 1 ? ' email address. ' : ' email addresses. '}
      <Button type='link' className='alertLinkText' onClick={handleReviewBtn}>
        Review
      </Button>
    </div>
  );
}

const DARK_WEB_WATCH_LIST_DATA: WatchlistDataType = {
  personalInfromation: {
    title: 'Personal information',
    qaSelector: 'component-dwm_personal-info',
    data: [
      {
        id: 'name',
        icon: 'user',
        maxMonitoredItems: 1,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-name',
      },
      {
        id: 'birthDate',
        icon: 'birthday',
        maxMonitoredItems: 1,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-dob',
      },
      {
        id: 'ssn',
        icon: 'ssn',
        maxMonitoredItems: 1,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-ssn',
      },
      {
        id: 'motherName',
        icon: 'adult',
        maxMonitoredItems: 1,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-mothers-maiden-name',
      },
      {
        id: 'email',
        icon: 'mail',
        maxMonitoredItems: 10,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-email',
      },
      {
        id: 'phone',
        icon: 'call',
        maxMonitoredItems: 10,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-phone',
      },
      {
        id: 'address',
        icon: 'home',
        maxMonitoredItems: 1,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-address',
      },
      {
        id: 'networkIP',
        icon: 'global-search',
        maxMonitoredItems: 10,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-ip-address',
      },
    ],
  },
  identification: {
    title: 'Identification',
    qaSelector: 'component-dwm_identification-info',
    data: [
      {
        id: 'license',
        icon: 'id',
        maxMonitoredItems: 10,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-drivers-license',
      },
      {
        id: 'insurance',
        icon: 'insurance',
        maxMonitoredItems: 10,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-health-insurance',
      },
      {
        id: 'passport',
        icon: 'passport',
        maxMonitoredItems: 10,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-passport',
      },
      {
        id: 'visa',
        icon: 'passport',
        maxMonitoredItems: 10,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-greencard',
      },
      {
        id: 'taxPayer',
        icon: 'document-user',
        maxMonitoredItems: 1,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-taxpayer-id',
      },
      {
        id: 'npi',
        icon: 'npi',
        maxMonitoredItems: 1,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-npi',
      },
    ],
  },
  financial: {
    title: 'Financial',
    qaSelector: 'component-dwm_financial-info',
    data: [
      {
        id: 'creditCard',
        icon: 'credit-card',
        maxMonitoredItems: 10,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-credit-card',
      },
      {
        id: 'accountNumber',
        icon: 'bank',
        maxMonitoredItems: 10,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-financial-account',
      },
      {
        id: 'cryptoWallet',
        icon: 'crypto-wallet',
        maxMonitoredItems: 10,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.cryptoWallet],
        qaSelector: 'component-dwm_add-crypto-wallet',
      },
      {
        id: 'loyalty',
        icon: 'loyalty-card',
        maxMonitoredItems: 10,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-loyalty-card',
      },
    ],
  },
  userName: {
    title: 'Username',
    qaSelector: 'component-dwm_username',
    data: [
      {
        id: 'gamertag',
        icon: 'controller',
        maxMonitoredItems: 2,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-gamertag',
      },
      {
        id: 'facebook',
        icon: 'facebook',
        maxMonitoredItems: 2,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-facebook',
      },
      {
        id: 'instagram',
        icon: 'instagram',
        maxMonitoredItems: 2,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-instagram',
      },
      {
        id: 'paypal',
        icon: 'paypal',
        maxMonitoredItems: 2,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-paypal',
      },
      {
        id: 'telegram',
        icon: 'telegram',
        maxMonitoredItems: 2,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-telegram',
      },
      {
        id: 'twitter',
        icon: 'x',
        maxMonitoredItems: 2,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-twitter',
      },
      {
        id: 'venmo',
        icon: 'venmo',
        maxMonitoredItems: 2,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-venmo',
      },
      {
        id: 'youtube',
        icon: 'youtube',
        maxMonitoredItems: 2,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-youtube',
      },
      {
        id: 'otherUsernames',
        icon: 'user-search',
        maxMonitoredItems: 10,
        entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.darkweb],
        qaSelector: 'component-dwm_add-others',
      },
    ],
  },
};

export const MEDICAL_WATCH_LIST_DATA: DataItemType[] = [
  {
    id: 'insurance',
    icon: 'insurance',
    maxMonitoredItems: 10,
    entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.medicalDataMonitoring],
    qaSelector: 'component-dwm_add-insurance',
  },
  {
    id: 'medicalRecordNumber',
    icon: 'medical-mrn',
    maxMonitoredItems: 9,
    entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.medicalDataMonitoring],
    qaSelector: 'component-dwm_add-mrn',
  },
  {
    id: 'medicalBeneficiaryIdentifier',
    icon: 'medical-mbi',
    maxMonitoredItems: 1,
    entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.medicalDataMonitoring],
    qaSelector: 'component-dwm_add-mbi',
  },
  {
    id: 'medicalCannabisID',
    icon: 'insurance',
    maxMonitoredItems: 1,
    entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.medicalCannabisID],
    qaSelector: 'component-dwm_add-cannabis-id',
  },
  {
    id: 'npi',
    icon: 'npi',
    maxMonitoredItems: 1,
    entitlement: SERVICE_ENTITLEMENTS_MAPPING[SERVICES.medicalDataMonitoring],
    qaSelector: 'component-dwm_add-npi',
  },
];

export const DARK_WEB_ITEMS_MAP: DarkWebItemsMapType = {
  name: {
    title: 'Name',
    itemType: 'name',
    addItemTitle: 'Add name',
    addItemSuccessMsg: 'Name added to Watchlist.',
    addItemFailureMsg:
      'Your name could not be added. Please wait and try again later.',
    whyMonitor:
      'Providing additional identifying information like name, address, and date of birth allows us to improve our matching, ensuring we alert you only about events that impact you.',
    removeItemTitle: 'Remove name from your Watchlist?',
    removeItemSubTitle:
      'If you remove your name from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Name removed from Watchlist.',
    removeItemFailureMsg:
      'Name could not be removed. Please wait and try again later.',
  },
  birthDate: {
    title: 'Date of birth',
    itemType: 'birth date',
    addItemTitle: 'Add date of birth',
    addItemSuccessMsg: 'Date of birth added to Watchlist',
    addItemFailureMsg:
      'Date of birth could not be added. Please wait and try again later.',
    whyMonitor:
      'Your date of birth is a critical piece of personally identifiable information. Fraudsters can use your date of birth and other personal information to access your accounts or open new accounts in your name.',
    removeItemTitle: 'Remove date of birth from your Watchlist?',
    removeItemSubTitle:
      'If you remove your date of birth from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Date of birth removed from Watchlist.',
    removeItemFailureMsg:
      'Date of birth could not be removed. Please wait and try again later.',
  },
  ssn: {
    title: 'Social Security Number (SSN)',
    itemType: 'ssn',
    addItemTitle: 'Add Social Security Number (SSN)',
    addItemSuccessMsg: 'Social Security Number (SSN) added to Watchlist.',
    addItemFailureMsg:
      'Social Security Number (SSN) could not be added. Please wait and try again later.',
    whyMonitor:
      'Your Social Security Number (SSN) is the most crucial piece of government-issued identification, making it very valuable to identity thieves. A fraudster can use your SSN and other personal information to open credit card and bank accounts, take out loans, apply for government benefits, file a tax return, receive medical care, and obtain a job in your name.',
    removeItemTitle: 'Remove Social Security Number (SSN) from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Social Security Number (SSN) from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg:
      'Social Security Number (SSN) removed from Watchlist.',
    removeItemFailureMsg:
      'Social Security Number (SSN) could not be removed. Please wait and try again later.',
  },
  motherName: {
    title: 'Mother’s maiden name',
    itemType: 'mothers maiden name',
    addItemTitle: "Add mother's maiden name",
    addItemSuccessMsg: "Mother's maiden name added to Watchlist.",
    addItemFailureMsg:
      "Mother's maiden name could not be added. Please wait and try again later.",
    whyMonitor:
      "Mother's maiden name is often used as a security question or verification method by financial institutions, credit card companies, and other organizations. If a thief can obtain this information, they may use it to reset passwords, open new accounts, or make unauthorized purchases.",
    removeItemTitle: "Remove mother's maiden name from your Watchlist?",
    removeItemSubTitle:
      "If you remove your mother's maiden name from your Watchlist, it will no longer be monitored.",
    removeItemSuccessMsg: "Mother's maiden name removed from Watchlist.",
    removeItemFailureMsg:
      "Mother's maiden name could not be removed. Please wait and try again later.",
  },
  email: {
    title: 'Email address',
    itemType: 'email',
    addItemTitle: 'Add email address',
    addItemSuccessMsg: 'Email address added to Watchlist.',
    addItemFailureMsg:
      'Email address could not be added. Please wait and try again later.',
    whyMonitor:
      'Besides your name, your email address is the most public piece of your digital identity. Your login credentials may be compromised if your email address is found together with a password on the dark web. A hacker can use this information to access any account associated with these credentials.',
    removeItemTitle: 'Remove email address from your Watchlist?',
    removeItemSubTitle:
      'If you remove this email address from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Email address removed from Watchlist.',
    removeItemFailureMsg:
      'Email address could not be removed. Please wait and try again later.',
  },
  phone: {
    title: 'Phone number',
    itemType: 'phone',
    addItemTitle: 'Add phone number',
    addItemSuccessMsg: 'Phone number added to Watchlist.',
    addItemFailureMsg:
      'Phone number could not be added. Please wait and try again later.',
    whyMonitor:
      'A criminal can use your phone number and other personal information for impersonation or fraud. For example, a hacker opens an account with a different carrier, then transfers your number to a new phone. From there, they could attempt to take over your email, financial, or social media accounts to access sensitive information.',
    removeItemTitle: 'Remove phone number from your Watchlist?',
    removeItemSubTitle:
      'If you remove phone number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Phone number removed from Watchlist.',
    removeItemFailureMsg:
      'Phone number could not be removed. Please wait and try again later.',
  },
  address: {
    title: 'Home address',
    itemType: 'address',
    addItemTitle: 'Add home address',
    addItemSuccessMsg: 'Home address added to Watchlist.',
    addItemFailureMsg:
      'Home address could not be added. Please wait and try again later.',
    whyMonitor:
      'A criminal can use your home address and other personal information for impersonation or fraud. Searching for just your name and home address in public records, a thief can find the additional personal information necessary to steal your identity.',
    removeItemTitle: 'Remove home address from your Watchlist?',
    removeItemSubTitle:
      'If you remove your home address from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Home address removed from Watchlist.',
    removeItemFailureMsg:
      'Home address could not be removed. Please wait and try again later.',
  },
  networkIP: {
    title: 'IP address',
    itemType: 'network ip',
    addItemTitle: 'Add IP address',
    addItemSuccessMsg: 'IP address added to Watchlist.',
    addItemFailureMsg:
      'IP address could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        When a fraudster obtains your IP address, they may use it to try and
        gain access to your accounts through a process known as &quot;IP address
        spoofing.&quot; The attacker makes it appear as though their own IP
        address is the same as yours, which may allow them to bypass some of the
        security measures in place to protect accounts.
        <br />
        <br />
        If you need help finding your IP address, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove IP address from your Watchlist?',
    removeItemSubTitle:
      'If you remove this IP address from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'IP address removed from Watchlist.',
    removeItemFailureMsg:
      'IP address could not be removed. Please wait and try again later.',
  },
  license: {
    title: 'Driver’s license number',
    itemType: 'driver license',
    addItemTitle: "Add driver's license number",
    addItemSuccessMsg: "Driver's license number added to Watchlist.",
    addItemFailureMsg:
      "Driver's license number could not be added. Please wait and try again later.",
    whyMonitor:
      "A criminal can use your driver's license number for impersonation or fraud. In many states, criminals can find your full name, address, date of birth, and social security number just by searching your driver's license number. Thieves can use your driver's license number to create a fake ID or commit check fraud. During a traffic stop, an impostor can give your driver's license number to a police officer, which could negatively impact your driving record. Law enforcement agencies report that 6% of traffic violations involve some form of driver's license fraud.",
    removeItemTitle: "Remove driver's license number from your Watchlist?",
    removeItemSubTitle:
      "If you remove your driver's license number from your Watchlist, it will no longer be monitored.",
    removeItemSuccessMsg: "Driver's license number removed from Watchlist.",
    removeItemFailureMsg:
      "Driver's license number could not be removed. Please wait and try again later.",
  },
  insurance: {
    title: 'Health insurance ID number',
    itemType: 'health info',
    addItemTitle: 'Add health insurance ID number',
    addItemSuccessMsg: 'Health insurance ID number added to Watchlist.',
    addItemFailureMsg:
      'Health insurance ID number could not be added. Please wait and try again later.',
    whyMonitor:
      'A criminal can use your health insurance ID number and other personal information for impersonation or fraud. A fraudster can secure medical services, get prescription drugs, or file insurance claims using your unique health insurance ID number. If the thief’s medical information gets mixed in your electronic health records, you may receive improper care. If the impostor sees a doctor and gets treatment, their unpaid medical bills could go to collections, negatively impacting your credit history.',
    removeItemTitle: 'Remove health insurance ID number from your Watchlist?',
    removeItemSubTitle:
      'If you remove your health insurance ID number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Health insurance ID number removed from Watchlist.',
    removeItemFailureMsg:
      'Health insurance ID number could not be removed. Please wait and try again later.',
  },
  passport: {
    title: 'Passport number',
    itemType: 'passport',
    addItemTitle: 'Add passport number',
    addItemSuccessMsg: 'Passport number added to Watchlist.',
    addItemFailureMsg:
      'Passport number could not be added. Please wait and try again later.',
    whyMonitor:
      'Having your passport exposed on the Dark Web may mean that your passport was stolen or copied. A stolen passport is especially dangerous because it can lead to identity theft on an international level. For example, a thief can alter your passport or forge a new one using your number and their photo to open financial accounts, transfer money, or travel internationally under your name.',
    removeItemTitle: 'Remove passport number from your Watchlist?',
    removeItemSubTitle:
      'If you remove your passport number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Passport number removed from Watchlist.',
    removeItemFailureMsg:
      'Passport number could not be removed. Please wait and try again later.',
  },
  visa: {
    title: 'Green Card or visa number',
    itemType: 'visa number',
    addItemTitle: 'Add Green Card or visa number',
    addItemSuccessMsg: 'Green Card or visa number added to Watchlist.',
    addItemFailureMsg:
      'Green Card or visa number could not be added. Please wait and try again later.',
    whyMonitor:
      'If your Green Card or visa number is stolen, it could be used for identity theft, fraud or other illegal activities. Protecting your personal information and being cautious about sharing it online or with strangers is important.',
    removeItemTitle: 'Remove Green Card or visa number from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Green Card or visa number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Green Card or visa number removed from Watchlist.',
    removeItemFailureMsg:
      'Green Card or visa number could not be removed. Please wait and try again later.',
  },
  taxPayer: {
    title: 'Taxpayer Identification Number (TIN)',
    itemType: 'tax id',
    addItemTitle: 'Add Taxpayer Identification Number (TIN)',
    addItemSuccessMsg:
      'Taxpayer Identification Number (TIN) added to Watchlist.',
    addItemFailureMsg:
      'Taxpayer Identification Number (TIN) could not be added. Please wait and try again later.',
    whyMonitor:
      'An Individual Taxpayer Identification Number (ITIN or TIN) is a tax processing number only available for certain nonresident and resident aliens, their spouses, and dependents who cannot get a Social Security Number (SSN). If your TIN is stolen, it could be used by the thief to open bank accounts, apply for credit, or file fraudulent tax returns. This can result in financial losses for you and can also damage your credit score.',
    removeItemTitle:
      'Remove  Taxpayer Identification Number (TIN) from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Taxpayer Identification Number (TIN) from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg:
      'Taxpayer Identification Number (TIN) removed from Watchlist.',
    removeItemFailureMsg:
      'Taxpayer Identification Number (TIN) could not be removed. Please wait and try again later.',
  },
  npi: {
    title: 'National Provider Identifier (NPI)',
    itemType: 'national provider identifier',
    addItemTitle: 'Add National Provider Identifier (NPI)',
    addItemSuccessMsg: 'National Provider Identifier (NPI) added to Watchlist.',
    addItemFailureMsg:
      'National Provider Identifier (NPI) could not be added. Please wait and try again later.',
    whyMonitor:
      'According to Physicians Practice, "Thousands of National Provider Identifiers (NPIs) are stolen from healthcare professionals and used for further fraudulent schemes every year, particularly Medicaid and Medicare fraud." Recovering from misuse of your NPI, including resolving potential financial liabilities, can be a long and difficult process.',
    removeItemTitle:
      'Remove National Provider Identifier (NPI) from your Watchlist?',
    removeItemSubTitle:
      'If you remove your National Provider Identifier (NPI) from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg:
      'National Provider Identifier (NPI) removed from Watchlist.',
    removeItemFailureMsg:
      'National Provider Identifier (NPI) could not be removed. Please wait and try again later.',
  },
  creditCard: {
    title: 'Credit card number',
    itemType: 'credit card',
    addItemTitle: 'Add credit card number',
    addItemSuccessMsg: 'Credit card number added to Watchlist.',
    addItemFailureMsg:
      'Credit card number could not be added. Please wait and try again later.',
    whyMonitor:
      'Credit card fraud is a multi-billion dollar a year industry. Criminals may use your credit card number to make purchases online or in stores. Many of these fraudulent purchases go undetected.',
    removeItemTitle: 'Remove credit card number from your Watchlist?',
    removeItemSubTitle:
      'If you remove your credit card number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Credit card number removed from Watchlist.',
    removeItemFailureMsg:
      'Credit card number could not be removed. Please wait and try again later.',
  },
  accountNumber: {
    title: 'Financial account number',
    itemType: 'financial account',
    addItemTitle: 'Add financial account number',
    addItemSuccessMsg: 'Financial account number added to Watchlist.',
    addItemFailureMsg:
      'Financial account number could not be added. Please wait and try again later.',
    whyMonitor:
      'A thief may use your financial account number for impersonation or fraud. With your financial account number and other personal information in hand, a fraudster can take over your account and drain your funds. Recovery from this type of financial fraud is often time-consuming and costly, especially if it is not detected quickly.',
    removeItemTitle: 'Remove financial account number from your Watchlist?',
    removeItemSubTitle:
      'If you remove this financial account number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Financial account number removed from Watchlist.',
    removeItemFailureMsg:
      'Financial account number could not be removed. Please wait and try again later.',
  },
  cryptoWallet: {
    title: 'Crypto wallet ID',
    itemType: 'crypto wallet',
    addItemTitle: 'Add crypto wallet ID',
    addItemSuccessMsg: 'Crypto wallet ID added to Watchlist.',
    addItemFailureMsg:
      'Crypto wallet ID could not be added. Please wait and try again later.',
    whyMonitor:
      'Crypto wallet addresses are public, but if they appear in data leaks or on the dark web alongside personal information, they may be targeted for fraud, phishing, or scams. Monitoring helps you stay informed and take action if your wallet information is exposed.',
    removeItemTitle: 'Remove crypto wallet ID from your Watchlist?',
    removeItemSubTitle:
      'If you remove this crypto wallet ID from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Crypto wallet ID removed from Watchlist.',
    removeItemFailureMsg:
      'Crypto wallet ID could not be removed. Please wait and try again later.',
  },
  loyalty: {
    title: 'Loyalty card number',
    itemType: 'loyalty card number',
    addItemTitle: 'Add loyalty card number',
    addItemSuccessMsg: 'Loyalty card number added to Watchlist.',
    addItemFailureMsg:
      'Loyalty card number could not be added. Please wait and try again later.',
    whyMonitor:
      'Loyalty points are essentially the equivalent of cash. They can be exchanged for goods and services and in some cases exchanged for money. As a result, fraudsters are increasingly targeting loyalty accounts.',
    removeItemTitle: 'Remove loyalty card number from your Watchlist?',
    removeItemSubTitle:
      'If you remove this loyalty card number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Loyalty card number removed from Watchlist.',
    removeItemFailureMsg:
      'Loyalty card number could not be removed. Please wait and try again later.',
  },
  gamertag: {
    title: 'Gamertag',
    itemType: 'gamer tag',
    addItemTitle: 'Add gamertag',
    addItemSuccessMsg: 'Gamertag added to Watchlist.',
    addItemFailureMsg:
      'Gamertag could not be added. Please wait and try again later.',
    whyMonitor:
      "A typical gaming account can contain a player's name, birth year, mailing address, email, mobile number, payment information, and other personal information that, if obtained by an identity thief, can cause financial damage. Cybercriminals also target gamer tags for virtual items to sell on the Dark Web. Once a gaming account is compromised, the player's other accounts, including banking and social media, are at a higher risk for account takeovers and fraud.",
    removeItemTitle: 'Remove gamertag from your Watchlist?',
    removeItemSubTitle:
      'If you remove your gamertag from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Gamertag removed from Watchlist.',
    removeItemFailureMsg:
      'Gamertag could not be removed. Please wait and try again later.',
  },
  facebook: {
    title: 'Facebook',
    itemType: 'online profile facebook',
    addItemTitle: 'Add Facebook username',
    addItemSuccessMsg: 'Facebook username added to Watchlist.',
    addItemFailureMsg:
      'Facebook username could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        You can sign in to your Facebook account using the email, phone number,
        or username that you signed up with, along with your password.
        Monitoring your Facebook account details, including your username, may
        help detect exposure of your Facebook account credentials. When your
        credentials are exposed the risk of unauthorized or fraudulent access to
        your account increases.
        <br />
        <br />
        If you need help finding your Facebook username, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove Facebook username from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Facebook username from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Facebook username removed from Watchlist.',
    removeItemFailureMsg:
      'Facebook username could not be removed. Please wait and try again later.',
  },
  instagram: {
    title: 'Instagram',
    itemType: 'online profile instagram',
    addItemTitle: 'Add Instagram username',
    addItemSuccessMsg: 'Instagram username added to Watchlist.',
    addItemFailureMsg:
      'Instagram username could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        Monitoring your Instagram account details, including your username, may
        help detect exposure of your account credentials. When your credentials
        are exposed the risk of unauthorized or fraudulent access to your
        account increases. It also means you&apos;re more likely to be a target
        of spamming, social engineering, and phishing attacks.
        <br />
        <br />
        If you need help finding your Instagram username, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove Instagram username from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Instagram username from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Instagram username removed from Watchlist.',
    removeItemFailureMsg:
      'Instagram username could not be removed. Please wait and try again later.',
  },
  paypal: {
    title: 'PayPal',
    itemType: 'online profile paypal',
    addItemTitle: 'Add PayPal username',
    addItemSuccessMsg: 'PayPal username added to Watchlist.',
    addItemFailureMsg:
      'PayPal username could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        PayPal allows users can link their bank account, credit card, or debit
        card to the platform and then use it to make payments online or through
        a mobile device
        <br />
        <br />
        Your PayPal username is a unique identifier that you choose when you
        create your PayPal account.
        <br />
        <br />
        Monitoring your PayPal account details, including your username, may
        help detect exposure of your account credentials. When your credentials
        are exposed the risk of unauthorized or fraudulent access to your
        account increases.
        <br />
        <br />
        If you need help finding your Paypal username, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove PayPal username from your Watchlist?',
    removeItemSubTitle:
      'If you remove your PayPal username from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'PayPal username removed from Watchlist.',
    removeItemFailureMsg:
      'PayPal username could not be removed. Please wait and try again later.',
  },
  telegram: {
    title: 'Telegram',
    itemType: 'online profile telegram',
    addItemTitle: 'Add Telegram username',
    addItemSuccessMsg: 'Telegram username added to Watchlist.',
    addItemFailureMsg:
      'Telegram username could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        Telegram is one of the most secure messaging apps you can use. Setting
        up a username on Telegram allows you to use the app without sharing your
        phone number with others.
        <br />
        <br />
        Monitoring your Telegram account details, including your username, may
        help detect exposure of your account credentials. When your credentials
        are exposed the risk of unauthorized or fraudulent access to your
        account increases.
        <br />
        <br />
        If you need help finding your Telegram username, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove Telegram username from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Telegram username from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Telegram username removed from Watchlist.',
    removeItemFailureMsg:
      'Telegram username could not be removed. Please wait and try again later.',
  },
  twitter: {
    title: 'X (Twitter)',
    itemType: 'online profile twitter',
    addItemTitle: 'Add X (Twitter) handle',
    addItemSuccessMsg: 'X (Twitter) handle added to Watchlist.',
    addItemFailureMsg:
      'X (Twitter) handle could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        Your X (Twitter) username, also known as your handle, begins with the
        &quot;@&quot; symbol, is unique to your account, and appears in your X
        (Twitter) profile URL. Your username is used to log in to your account,
        and is visible when sending and receiving replies and Direct Messages.
        <br />
        <br />
        Monitoring your X (Twitter) account details, including your handle, may
        help detect exposure of your X (Twitter) account credentials. When your
        credentials are exposed the risk of unauthorized or fraudulent access to
        your account increases.
        <br />
        <br />
        If you need help finding your X (Twitter) handle, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove X (Twitter) handle from your Watchlist?',
    removeItemSubTitle:
      'If you remove your X (Twitter) handle from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'X (Twitter) handle removed from Watchlist.',
    removeItemFailureMsg:
      'X (Twitter) handle could not be removed. Please wait and try again later.',
  },
  venmo: {
    title: 'Venmo',
    itemType: 'online profile venmo',
    addItemTitle: 'Add Venmo username',
    addItemSuccessMsg: 'Venmo username added to Watchlist.',
    addItemFailureMsg:
      'Venmo username could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        You can sign in to your Venmo account using the email, phone number, or
        username that you signed up with, along with your password. Monitoring
        your Venmo account, including your username, may help detect exposure of
        your Venmo account credentials, putting you at risk of unauthorized or
        fraudulent access to your account.
        <br />
        <br />
        If you need help finding your Venmo username, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove Venmo username from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Venmo username from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Venmo username removed from Watchlist.',
    removeItemFailureMsg:
      'Venmo username could not be removed. Please wait and try again later.',
  },
  youtube: {
    title: 'YouTube',
    itemType: 'online profile youtube',
    addItemTitle: 'Add YouTube handle',
    addItemSuccessMsg: 'YouTube handle added to Watchlist.',
    addItemFailureMsg:
      'YouTube handle could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        Monitoring your YouTube account details, including your handle, may help
        detect exposure of your account credentials. When your credentials are
        exposed the risk of unauthorized or fraudulent access to your account
        increases. It also means you&apos;re more likely to be a target of
        spamming, social engineering, and phishing attacks.
        <br />
        <br />
        If you need help finding your YouTube handle, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove YouTube handle from your Watchlist?',
    removeItemSubTitle:
      'If you remove your YouTube handle from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'YouTube handle removed from Watchlist.',
    removeItemFailureMsg:
      'YouTube handle could not be removed. Please wait and try again later.',
  },
  otherUsernames: {
    title: 'Other usernames',
    itemType: 'username',
    addItemTitle: 'Add username',
    addItemSuccessMsg: 'Username added to Watchlist.',
    addItemFailureMsg:
      'Username could not be added. Please wait and try again later.',
    whyMonitor:
      'While many sites use your email address as a login credential, others require you to create a username. For example, many banks and other financial institutions use a username instead of an email address to access online account information and make transactions. By registering your username, we can notify you if we find it exposed on the Dark Web.',
    removeItemTitle: 'Remove username from your Watchlist?',
    removeItemSubTitle:
      'If you remove this username from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Username removed from Watchlist.',
    removeItemFailureMsg:
      'Username could not be removed. Please wait and try again later.',
  },
  medicalRecordNumber: {
    title: 'Medical Record Number (MRN)',
    itemType: 'medical record number',
    addItemTitle: 'Add Medical Record Number (MRN)',
    addItemSuccessMsg: 'Medical Record Number (MRN) added to Watchlist.',
    addItemFailureMsg:
      'Medical Record Number (MRN) could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        Medical record numbers (MRNs) are unique identifiers assigned to
        patients by healthcare organizations to keep track of their medical
        records. These numbers can be used to link all the different parts of a
        patient&apos;s medical history, including their test results,
        medications, treatments, and other personal information.
        <br />
        <br />
        We recommend monitoring your medical record number because the records
        often contain sensitive information such as Social Security numbers,
        insurance policy numbers, and credit card information that can be used
        for identity theft. Additionally, your medical history is extremely
        personal and you should be aware if it has been exposed.
        <br />
        <br />
        It&apos;s important to note that MRNs are not unique across all
        healthcare organizations, and they may not be shared between
        organizations. If you receive treatment from multiple healthcare
        providers, you may have multiple MRNs, one for each provider you see.
      </>
    ),
    removeItemTitle: 'Remove Medical Record Number (MRN) from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Medical Record Number (MRN) from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Medical Record Number (MRN) removed from Watchlist.',
    removeItemFailureMsg:
      'Medical Record Number (MRN) could not be removed. Please wait and try again later.',
  },
  medicalBeneficiaryIdentifier: {
    title: 'Medicare Beneficiary Identifier (MBI)',
    itemType: 'medical beneficiary identifier',
    addItemTitle: 'Add Medicare Beneficiary Identifier (MBI)',
    addItemSuccessMsg:
      'Medicare Beneficiary Identifier (MBI) added to Watchlist.',
    addItemFailureMsg:
      'Medicare Beneficiary Identifier (MBI) could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        Medicare Beneficiary Identifier (MBI) is the number assigned to Medicare
        beneficiaries by the Centers for Medicare and Medicaid Services (CMS) to
        identify the beneficiary. Monitoring the dark web for your MBI is
        important because it can be used to access sensitive personal
        information such as medical records, financial information, and personal
        identifying information. If your MBI is found on the dark web, it may
        indicate that your data has been compromised and is being sold or used
        for illegal activities. By monitoring the dark web for your MBI, you can
        identify if your personal information has been compromised and take
        appropriate action to protect yourself.
      </>
    ),
    removeItemTitle:
      'Remove Medicare Beneficiary Identifier (MBI) from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Medicare Beneficiary Identifier (MBI) from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg:
      'Medicare Beneficiary Identifier (MBI) removed from Watchlist.',
    removeItemFailureMsg:
      'Medicare Beneficiary Identifier (MBI) could not be removed. Please wait and try again later.',
  },
  medicalCannabisID: {
    title: 'Medical cannabis ID number',
    itemType: 'medical cannabis id',
    addItemTitle: 'Add medical cannabis ID number',
    addItemSuccessMsg: 'Medical cannabis ID number added to Watchlist.',
    addItemFailureMsg:
      'Medical cannabis ID number could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        A medical cannabis ID number is a unique identifier linked to your
        registration and eligibility for medical cannabis programs. Monitoring
        the dark web for your ID is important because it may be used to access
        sensitive personal information, including your identity details, medical
        status, and associated records. If your Medical Cannabis ID number
        appears on the dark web, it could indicate that your information has
        been exposed and may be misused or sold. By monitoring for this data,
        you can detect potential compromises early and take steps to protect
        your identity and personal information.
      </>
    ),
    removeItemTitle: 'Remove medical cannabis ID number from your Watchlist?',
    removeItemSubTitle:
      'If you remove your medical cannabis ID number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Medical cannabis ID number removed from Watchlist.',
    removeItemFailureMsg:
      'Medical cannabis ID number could not be removed. Please wait and try again later.',
  },
};

export const EXCLUDE_WATCH_LIST_ITEMS_FOR_PS_GLOBAL: WatchlistCategoryType[] = [
  'ssn',
  'address',
  'license',
  'visa',
  'taxPayer',
  'npi',
  'loyalty',
];

export const EXCLUDE_WATCH_LIST_ITEMS_FOR_HEALTH_CARE_FEATURE: WatchlistCategoryType[] =
  ['insurance', 'npi'];

// Child-mode section list — mirrors Angular's hardcoded child branch
// in `watchlist-dark-web-new.component.ts:213-346`. The dark-web view
// for a child shows a smaller catalogue than the adult view; every
// other section is hidden. Kept as a positive allowlist (not an
// EXCLUDE_* list) so the child list stays in one place rather than
// being expressed as "everything except X".
export const CHILD_MODE_WATCH_LIST_ITEMS: WatchlistCategoryType[] = [
  'name',
  'birthDate',
  'ssn',
  'email',
  'phone',
  'address',
  'insurance',
  'license',
  'passport',
  'accountNumber',
  'gamertag',
];

// Child-mode per-item max counts. Angular's child branch declares
// its own `numAllowed` per field (`watchlist-dark-web-new.component.ts:238-344`)
// which differ from adult limits for six items: email (2 vs 10),
// phone (1 vs 10), license (1 vs 10), passport (2 vs 10),
// accountNumber (5 vs 10 in Angular / 5 in ours), gamertag (10 vs 2
// in ours). Only fields whose child value differs from React's
// `maxMonitoredItems` default appear here — everything else stays on
// the catalogue default.
export const CHILD_MODE_MAX_MONITORED_ITEMS: Partial<
  Record<WatchlistCategoryType, number>
> = {
  email: 2,
  phone: 1,
  license: 1,
  passport: 2,
  accountNumber: 5,
  gamertag: 10,
};

export type WatchlistDataKeyType = keyof WatchlistDataType;
export default DARK_WEB_WATCH_LIST_DATA;
